import { useState, FormEvent } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { PublicNavbar } from '../components/layout/PublicNavbar';
import { AuroraBG } from '../components/ui/AuroraBG';
import { GlassPanel } from '../components/ui/GlassPanel';
import { Input } from '../components/ui/Input';
import { Button } from '../components/ui/Button';
import { ChefHat } from 'lucide-react';

export function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const { signIn } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      const { error } = await signIn(email, password);
      if (error) {
        setError(error.message);
      } else {
        navigate('/dashboard');
      }
    } catch (err) {
      setError('An unexpected error occurred');
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e: FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      setSuccess('Password reset email sent! Check your inbox.');
      setTimeout(() => {
        setShowForgotPassword(false);
        setResetEmail('');
        setSuccess('');
      }, 3000);
    } catch (err) {
      setError('An unexpected error occurred');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <AuroraBG />
      <PublicNavbar />
      <div className="min-h-screen flex items-center justify-center p-4 pt-20">
        <GlassPanel className="w-full max-w-md p-8">
          <div className="flex items-center justify-center mb-8">
            <ChefHat className="w-12 h-12 text-primary-400 mr-3" />
            <h1 className="text-3xl font-bold text-white">EL-OS</h1>
          </div>

          {!showForgotPassword ? (
            <>
              <h2 className="text-2xl font-bold text-white mb-6 text-center">
                Login
              </h2>

              <form onSubmit={handleSubmit} className="space-y-4">
                <Input
                  label="Email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@company.com"
                  required
                />

                <Input
                  label="Password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                />

                {error && (
                  <div className="p-3 rounded-lg bg-error-500/20 border border-error-500/50 text-error-200 text-sm">
                    {error}
                  </div>
                )}

                {success && (
                  <div className="p-3 rounded-lg bg-success-500/20 border border-success-500/50 text-success-200 text-sm">
                    {success}
                  </div>
                )}

                <Button
                  type="submit"
                  variant="primary"
                  className="w-full"
                  disabled={loading}
                >
                  {loading ? 'Signing in...' : 'Sign In'}
                </Button>
              </form>

              <div className="mt-4 text-center">
                <button
                  onClick={() => setShowForgotPassword(true)}
                  className="text-sm text-primary-400 hover:text-primary-300 font-medium"
                >
                  Forgot password?
                </button>
              </div>

              <p className="mt-6 text-center text-white/60 text-sm">
                Don't have an account?{' '}
                <Link to="/register" className="text-primary-400 hover:text-primary-300 font-medium">
                  Sign up
                </Link>
              </p>
            </>
          ) : (
            <>
              <h2 className="text-2xl font-bold text-white mb-2 text-center">
                Reset Password
              </h2>
              <p className="text-white/60 text-sm text-center mb-6">
                Enter your email address and we'll send you a link to reset your password.
              </p>

              <form onSubmit={handleForgotPassword} className="space-y-4">
                <Input
                  label="Email"
                  type="email"
                  value={resetEmail}
                  onChange={(e) => setResetEmail(e.target.value)}
                  placeholder="you@company.com"
                  required
                />

                {error && (
                  <div className="p-3 rounded-lg bg-error-500/20 border border-error-500/50 text-error-200 text-sm">
                    {error}
                  </div>
                )}

                {success && (
                  <div className="p-3 rounded-lg bg-success-500/20 border border-success-500/50 text-success-200 text-sm">
                    {success}
                  </div>
                )}

                <Button
                  type="submit"
                  variant="primary"
                  className="w-full"
                  disabled={loading}
                >
                  {loading ? 'Sending...' : 'Send Reset Link'}
                </Button>

                <button
                  type="button"
                  onClick={() => {
                    setShowForgotPassword(false);
                    setError('');
                    setSuccess('');
                    setResetEmail('');
                  }}
                  className="w-full text-sm text-white/60 hover:text-white font-medium"
                >
                  Back to Sign In
                </button>
              </form>
            </>
          )}
        </GlassPanel>
      </div>
    </>
  );
}
